import { useTelemetry } from './useTelemetry'

interface TelemetryLike {
  trackException: (description: string, properties?: Record<string, unknown>) => void
  trackPerformance: (metric: string, value: number, properties?: Record<string, unknown>) => void
}

interface ListenerOptions {
  getPath?: () => string
  telemetry?: TelemetryLike
}

interface PageLoadPerformanceOptions extends ListenerOptions {
  getPerformanceEntries?: () => PerformanceNavigationTiming[]
}

let uninstallTelemetryListeners: (() => void) | null = null

const getDefaultPath = (): string => {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

const getDefaultNavigationEntries = (): PerformanceNavigationTiming[] => {
  if (typeof performance === 'undefined') return []
  return performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
}

const getTelemetry = (telemetry?: TelemetryLike): TelemetryLike => {
  return telemetry ?? useTelemetry()
}

export const trackPageLoadPerformance = ({
  getPath = getDefaultPath,
  getPerformanceEntries = getDefaultNavigationEntries,
  telemetry,
}: PageLoadPerformanceOptions = {}) => {
  const navigationEntry = getPerformanceEntries()[0]
  const duration = Number(navigationEntry?.duration)

  if (!Number.isFinite(duration) || duration <= 0) return

  getTelemetry(telemetry).trackPerformance('page_load', Math.round(duration), {
    navigation_type: navigationEntry.type || 'unknown',
    page_path: getPath(),
  })
}

export const installTelemetryListeners = ({ getPath = getDefaultPath, telemetry }: ListenerOptions = {}) => {
  if (typeof window === 'undefined') return () => {}
  if (uninstallTelemetryListeners) return uninstallTelemetryListeners

  const resolvedTelemetry = getTelemetry(telemetry)
  const trackWindowError = () => {
    resolvedTelemetry.trackException('window_error', {
      fatal: false,
      page_path: getPath(),
      source: 'window',
    })
  }
  const trackUnhandledRejection = () => {
    resolvedTelemetry.trackException('unhandled_rejection', {
      fatal: false,
      page_path: getPath(),
      source: 'promise',
    })
  }
  const trackLoadPerformance = () => {
    trackPageLoadPerformance({
      getPath,
      telemetry: resolvedTelemetry,
    })
  }

  window.addEventListener('error', trackWindowError)
  window.addEventListener('unhandledrejection', trackUnhandledRejection)

  if (document.readyState === 'complete') {
    window.setTimeout(trackLoadPerformance, 0)
  } else {
    window.addEventListener('load', trackLoadPerformance, { once: true })
  }

  uninstallTelemetryListeners = () => {
    window.removeEventListener('error', trackWindowError)
    window.removeEventListener('unhandledrejection', trackUnhandledRejection)
    window.removeEventListener('load', trackLoadPerformance)
    uninstallTelemetryListeners = null
  }

  return uninstallTelemetryListeners
}
